import { PropsWithChildren, useEffect } from 'react';
import { makeStyles, IconButton, Tooltip } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import AppsIcon from '@material-ui/icons/Apps';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Sidebar,
  sidebarConfig,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  useSidebarOpenState,
  useSidebarPinState,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';

// Inline pushpin SVG (not available in MUI v4 icons)
const PushPinIcon = ({ filled }: { filled: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    style={{ display: 'block' }}
  >
    {filled ? (
      <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
    ) : (
      <path d="M17 4v8l2 2v2h-6v6h-2v-6H5v-2l2-2V4H6V2h12v2h-1zm-2 8.17V4H9v8.17l-1.36 1.36-.11.47H16.47l-.11-.47L15 12.17z" />
    )}
  </svg>
);

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  rootOpen: {
    width: sidebarConfig.drawerWidthOpen,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
    paddingRight: 8,
  },
  link: {
    flex: 1,
    marginLeft: 24,
  },
  pinButton: {
    color: '#64748B',
    padding: 4,
    '&:hover': {
      color: '#F1F5F9',
      backgroundColor: '#1E293B',
    },
  },
  pinButtonActive: {
    color: '#0EA5E9',
    padding: 4,
    '&:hover': {
      color: '#38BDF8',
      backgroundColor: '#1E293B',
    },
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();
  const { isPinned, toggleSidebarPinState } = useSidebarPinState();

  return (
    <div className={isOpen ? classes.rootOpen : classes.root}>
      <a
        href="/"
        className={classes.link}
        aria-label="Home"
        style={{ textDecoration: 'none' }}
      >
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </a>
      {isOpen && (
        <Tooltip
          title={isPinned ? 'Unpin sidebar' : 'Pin sidebar'}
          placement="right"
        >
          <IconButton
            size="small"
            onClick={toggleSidebarPinState}
            className={isPinned ? classes.pinButtonActive : classes.pinButton}
          >
            <PushPinIcon filled={isPinned} />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  useEffect(() => {
    if (localStorage.getItem('sidebar-is-pinned') === null) {
      localStorage.setItem('sidebar-is-pinned', 'false');
    }
  }, []);

  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          <SidebarItem icon={HomeIcon} to="home" text="Home" />
          <SidebarItem icon={AppsIcon} to="catalog" text="Catalog" />
          <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
          <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        </SidebarGroup>
        <SidebarSpace />
      </Sidebar>
      {children}
    </SidebarPage>
  );
};
